import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Divider,
} from "@mui/material";
import { LockOutlined, CreditCard, CheckCircleOutline } from "@mui/icons-material";
import api from "../api";
import { refreshStudentPortalMode } from "../hooks/useStudentPortalMode";
import useStudentPlanCheckout, {
  formatPlanPrice as formatPrice,
  isFreePlan,
} from "../hooks/useStudentPlanCheckout";
import {
  formatListPrice,
  formatMoney,
  isRecommendedPlan,
  isRenewalPlan,
  planDurationLabel,
  renewalHeadline,
} from "../utils/mobilePlanCatalogue";
import { renewalSentence, trialTermsShort } from "../content/trialTerms";
import { formatTrialEnd } from "../utils/mobileSubscriptionFormat";

const CARD_FAILURE_TYPES = new Set([
  "trial_auto_upgrade_payment_failed",
  "subscription_renewal_failed",
]);

/**
 * Unpaid mobile students see this instead of locked learning pages.
 * They pick a plan (preferred plan preselected) and pay via Stripe, or start a free trial.
 *
 * The plan-loading and checkout handshake lives in useStudentPlanCheckout, shared with the
 * student Billing page; this component owns only the full-screen lock presentation.
 */
const TRIAL_LOCK_TYPES = new Set([
  "trial_unsubscribed",
  "trial_expired_card_required",
  "free_plan_ended",
  // The window after a trial: access has already stopped, so it reads as an ended trial.
  "trial_ended_pending",
]);

/**
 * @param {"choose"|"plan"} initialStep "choose" asks first whether to just buy items or
 *   renew; "plan" opens the plan list straight away (the student already asked for plans,
 *   or is back from a plan checkout).
 */
const StudentSubscriptionLock = ({ lock, title = "Choose a plan to continue", initialStep = "choose" }) => {
  const navigate = useNavigate();
  // Step 1 is the decision alone; the plan list only appears once they pick a plan. A lapsed
  // student used to land straight on the plan list, which read as "pay or leave" even though
  // single mock exams, past papers and courses never needed a plan.
  // An ended free trial has no "just buy items" option: plans are the only way on.
  const plansOnly = lock?.catalogueLocked === true;
  const [step, setStep] = useState(initialStep === "plan" || plansOnly ? "plan" : "choose");
  const [choice, setChoice] = useState("items"); // "items" | "plan"
  const autoOpenedPortalRef = useRef(false);

  const {
    plans,
    trialTerms,
    selectedPlan,
    selectedPlanId,
    setSelectedPlanId,
    preferredPlanId,
    loadingPlans,
    busy,
    fulfilling,
    error,
    openBillingPortal,
    continueWithPlan,
    retainedPrice,
    renewal,
  } = useStudentPlanCheckout({
    preferredPlanId: lock?.preferredPlanId,
    redirectTo: "/student",
  });

  // Expired / insufficient card: open Stripe Add payment method immediately.
  useEffect(() => {
    if (autoOpenedPortalRef.current) return;
    if (!lock?.portalAvailable) return;
    if (!CARD_FAILURE_TYPES.has(String(lock?.type || ""))) return;
    autoOpenedPortalRef.current = true;
    void openBillingPortal();
  }, [lock?.portalAvailable, lock?.type, openBillingPortal]);

  // The student portal starts a trial without a card, so say that rather than repeating
  // the app's card-required wording.
  const message =
    step === "choose"
      ? "You can keep buying single mock exams, practice papers and courses without a plan, or renew your Mobile Plan to unlock everything."
      : lock?.message || trialTermsShort(trialTerms);

  // Their card failed. They are not buying anything — they already own this plan — so the
  // only thing to do here is fix the card. Showing the plan list instead is what made an
  // early adopter on £9.99 discover they were being asked for today's £29.99.
  const needsCardUpdate = lock?.recoveryAction === "payment_method_update";
  const owedPrice = lock?.retainedPrice || retainedPrice || null;

  // A plan that ran out is a renewal, not a fresh decision — say so, and preselect the plan
  // they were on. A cancellation is a fresh decision, so it keeps the neutral title.
  const trialEnded = TRIAL_LOCK_TYPES.has(String(lock?.type || ""));
  // A running trial is normally unlocked; kept for older APIs that still lock it.
  const trialEndsLabel = lock?.isTrial ? formatTrialEnd(lock?.trialEndsAt) : "";
  const heading =
    step === "choose"
      ? lock?.isTrial
        ? `Your free trial ends ${trialEndsLabel || "soon"}`
        : trialEnded
        ? "Your trial has ended"
        : "Your Mobile Plan has ended"
      : renewalHeadline(renewal) || title;

  const confirmChoice = () => {
    // Remembered server-side so the question is asked once for this lapse. Never blocks the
    // student: if saving fails they are simply asked again next time.
    api
      .post("student/subscription/lapsed-choice", { choice })
      .catch(() => {})
      .finally(refreshStudentPortalMode);
    if (choice === "plan") {
      setStep("plan");
      return;
    }
    // Just buying: the course dashboard (Dashboard.jsx picks it while the plan is locked),
    // where mock exams, past papers, courses and anything already bought are all reachable.
    navigate("/student");
  };


  if (fulfilling) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", px: { xs: 0, sm: 2 }, py: { xs: 2, md: 8 } }}>
        <Paper
          elevation={0}
          sx={{
            maxWidth: 520,
            width: "100%",
            p: { xs: 2, sm: 4 },
            borderRadius: 3,
            textAlign: "center",
            border: "1px solid #e5e7eb",
          }}
        >
          <CircularProgress sx={{ mb: 2 }} />
          <Typography fontWeight={700}>Confirming your payment…</Typography>
        </Paper>
      </Box>
    );
  }

  // One screen, one button. Everything else on this page is a decision, and a declined card
  // is not a decision — it is a repair. Agreed in the 22 Sep 2026 review as the minimum-
  // interaction path: update the card, we charge the usual amount and the plan comes back.
  if (needsCardUpdate) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", px: { xs: 0, sm: 2 }, py: { xs: 2, md: 8 } }}>
        <Paper
          elevation={0}
          sx={{
            maxWidth: 520,
            width: "100%",
            p: { xs: 2, sm: 4 },
            borderRadius: 3,
            textAlign: "center",
            border: "1px solid #e5e7eb",
            boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              mx: "auto",
              mb: 2,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(211,47,47,0.08)",
            }}
          >
            <CreditCard sx={{ color: "#d32f2f", fontSize: 32 }} />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
            Update your payment details
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
            {message}
          </Typography>

          {owedPrice ? (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                border: "1px solid #e5e7eb",
                backgroundColor: "#fafafa",
                textAlign: "left",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Your plan
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {owedPrice.plan_name || "Your plan"}
                {owedPrice.billing_interval
                  ? ` · ${planDurationLabel({ billing_interval: owedPrice.billing_interval })}`
                  : ""}
              </Typography>
              <Typography sx={{ fontWeight: 800, color: "#667eea", fontSize: "1.25rem", mt: 0.5 }}>
                {formatMoney(owedPrice.price_pence, owedPrice.currency)}
              </Typography>
              {/* An early adopter needs to be told, in the moment, that they keep their rate —
                  this is the reassurance that stops the "why am I being charged more?" email. */}
              {owedPrice.is_legacy ? (
                <Typography variant="caption" color="text.secondary">
                  This is your original price and it will not change.
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ mb: 2, textAlign: "left", borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={busy}
            onClick={() => openBillingPortal("payment_method_update")}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CreditCard />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              py: 1.4,
              background: "linear-gradient(90deg, #667eea, #764ba2)",
              "&:hover": { opacity: 0.92 },
            }}
          >
            {busy ? "Opening secure page…" : "Update payment details"}
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
            We will restart your plan as soon as the new card is saved.
          </Typography>

          <Button
            variant="text"
            size="small"
            sx={{ mt: 2, textTransform: "none", color: "text.secondary" }}
            onClick={() => {
              localStorage.clear();
              navigate("/login");
            }}
          >
            Sign out
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", px: { xs: 0, sm: 2 }, py: { xs: 2, md: 8 } }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 560,
          width: "100%",
          p: { xs: 2, sm: 4 },
          borderRadius: 3,
          textAlign: "center",
          border: "1px solid #e5e7eb",
          boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            mx: "auto",
            mb: 2,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <LockOutlined sx={{ color: "#fff", fontSize: 32 }} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
          {heading}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          {message}
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2, textAlign: "left", borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        {step === "choose" ? (
          <Box sx={{ textAlign: "left" }}>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>How would you like to continue?</Typography>
            <RadioGroup value={choice} onChange={(e) => setChoice(e.target.value)}>
              <FormControlLabel
                value="items"
                control={<Radio />}
                sx={{ alignItems: "flex-start", mb: 1 }}
                label={
                  <Box sx={{ pt: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Just buy mock exams, practice papers and courses
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      No plan needed. Pay only for what you want. Anything you have already
                      bought stays yours.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="plan"
                control={<Radio />}
                sx={{ alignItems: "flex-start" }}
                label={
                  <Box sx={{ pt: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Renew my Mobile Plan
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Every mock exam and practice paper included, plus worksheets, the wizard,
                      flash cards and games.
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={confirmChoice}
              sx={{
                mt: 2.5,
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                py: 1.4,
                background: "linear-gradient(90deg, #667eea, #764ba2)",
                "&:hover": { opacity: 0.92 },
              }}
            >
              {choice === "plan" ? "Continue to plans" : "Continue to my dashboard"}
            </Button>
          </Box>
        ) : (
        <>
        {!plansOnly && (
        <Box sx={{ textAlign: "left", mb: 1.5, mt: -1.5 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => setStep("choose")}
            sx={{ textTransform: "none", fontWeight: 600, px: 0 }}
          >
            ← Other options
          </Button>
        </Box>
        )}
        {loadingPlans ? (
          <CircularProgress size={32} sx={{ my: 3 }} />
        ) : plans.length === 0 ? (
          <Alert severity="warning" sx={{ textAlign: "left", borderRadius: 2 }}>
            No subscription plans are available. Please contact support.
          </Alert>
        ) : (
          <Stack spacing={1.25} sx={{ mb: 3, textAlign: "left" }}>
            {plans.map((plan) => {
              // A plan this account may not take (a used free trial, or one below their
              // floor) is shown greyed with the reason rather than hidden, so the buyer
              // can see it exists and why it is closed to them.
              const blocked = plan.selectable === false;
              const active = !blocked && Number(plan.id) === Number(selectedPlanId);
              const preferred = !blocked && Number(plan.id) === Number(preferredPlanId);
              return (
                <Box
                  key={plan.id}
                  onClick={() => {
                    if (blocked) return;
                    setSelectedPlanId(plan.id);
                  }}
                  aria-disabled={blocked || undefined}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: { xs: 1.25, sm: 1.5 },
                    borderRadius: 2,
                    border: active ? "2px solid #667eea" : "1px solid #e5e7eb",
                    backgroundColor: blocked
                      ? "#f7f7f8"
                      : active
                        ? "rgba(102,126,234,0.06)"
                        : "#fff",
                    opacity: blocked ? 0.6 : 1,
                    cursor: blocked ? "not-allowed" : "pointer",
                  }}
                >
                  <Radio
                    checked={active}
                    disabled={blocked}
                    onChange={() => setSelectedPlanId(plan.id)}
                    sx={{ p: 0.5 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                      {plan.name}
                      {isRenewalPlan(plan, renewal) ? (
                        <Typography
                          component="span"
                          sx={{
                            // Own line on phones so a long label can't run into the price.
                            display: { xs: "block", sm: "inline" },
                            ml: { xs: 0, sm: 1 },
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            color: "#16A34A",
                          }}
                        >
                          Your previous plan
                        </Typography>
                      ) : !blocked && isRecommendedPlan(plan, renewal) ? (
                        <Typography
                          component="span"
                          sx={{
                            // Own line on phones so a long label can't run into the price.
                            display: { xs: "block", sm: "inline" },
                            ml: { xs: 0, sm: 1 },
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            color: "#16A34A",
                          }}
                        >
                          Recommended
                        </Typography>
                      ) : preferred ? (
                        <Typography
                          component="span"
                          sx={{
                            // Own line on phones so a long label can't run into the price.
                            display: { xs: "block", sm: "inline" },
                            ml: { xs: 0, sm: 1 },
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            color: "#667eea",
                          }}
                        >
                          Your selection
                        </Typography>
                      ) : null}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {blocked
                        ? plan.blockedReason
                        : plan.description ||
                          (isFreePlan(plan)
                            ? trialTermsShort(trialTerms)
                            : renewalSentence(plan))}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right", whiteSpace: "nowrap", flexShrink: 0 }}>
                    {/* While the early-adopter window is open, show what they pay next to what
                        it normally costs — a discount nobody can see is not a discount. */}
                    {formatListPrice(plan) ? (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", color: "text.secondary", textDecoration: "line-through" }}
                      >
                        {formatListPrice(plan)}
                      </Typography>
                    ) : null}
                    <Typography sx={{ fontWeight: 800, color: "#667eea" }}>
                      {formatPrice(plan)}
                    </Typography>
                    {!isFreePlan(plan) ? (
                      <Typography variant="caption" color="text.secondary">
                        {planDurationLabel(plan)}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}

        <Button
          variant="contained"
          size="large"
          fullWidth
          disabled={busy || loadingPlans || !selectedPlan}
          onClick={() => continueWithPlan()}
          startIcon={
            busy ? (
              <CircularProgress size={16} color="inherit" />
            ) : selectedPlan && isFreePlan(selectedPlan) ? (
              <CheckCircleOutline />
            ) : (
              <CreditCard />
            )
          }
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            py: 1.4,
            background: "linear-gradient(90deg, #667eea, #764ba2)",
            "&:hover": { opacity: 0.92 },
          }}
        >
          {busy
            ? "Please wait…"
            : selectedPlan && isFreePlan(selectedPlan)
              ? "Start free trial"
              : isRenewalPlan(selectedPlan, renewal)
                ? `Renew ${selectedPlan.name}`
                : "Continue to payment"}
        </Button>
        </>
        )}

        {/* Billing recovery belongs with the plans, not with the "how to continue" question. */}
        {step === "plan" && lock?.portalAvailable ? (
          <>
            <Divider sx={{ my: 2.5 }}>
              <Typography variant="caption" color="text.secondary">
                Already subscribed?
              </Typography>
            </Divider>
            <Button
              variant="outlined"
              size="medium"
              disabled={busy}
              onClick={() => openBillingPortal()}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Add payment method on Stripe
            </Button>
          </>
        ) : null}

        <Button
          variant="text"
          size="small"
          sx={{ mt: 2, textTransform: "none", color: "text.secondary" }}
          onClick={() => {
            localStorage.clear();
            navigate("/login");
          }}
        >
          Sign out
        </Button>
      </Paper>
    </Box>
  );
};

export default StudentSubscriptionLock;
